import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { isLocal, getLocal, delLocals } from "@/lib/localPhotos";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

const NO_REFRESH = /\/auth\/(login|register|pin-login|refresh|logout)/;
let refreshing = null;
const doRefresh = () => {
  refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
  return refreshing;
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    if (err.response?.status === 401 && !cfg._retry && !NO_REFRESH.test(cfg.url || "")) {
      cfg._retry = true;
      try {
        await doRefresh();
        return api(cfg);
      } catch (e) {
        window.dispatchEvent(new Event("ka-logout"));
      }
    }
    return Promise.reject(err);
  }
);

export function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const errMsg = (e) => (e && !e.response && e.isAxiosError ? "No signal. Please try again when you're back online." : e && !e.response && e.message ? e.message : formatDetail(e?.response?.data?.detail));

const isNetErr = (e) => !e?.response;
const CACHED = ["/checks/today", "/equipment", "/temps/kinds", "/temps"];
const QK = "ka_queue";

export const readQueue = () => JSON.parse(localStorage.getItem(QK) || "[]");
const writeQueue = (q) => {
  localStorage.setItem(QK, JSON.stringify(q));
  window.dispatchEvent(new Event("ka-queue"));
};

const PMAP = "ka_photo_map";
const photoMap = () => JSON.parse(localStorage.getItem(PMAP) || "{}");

export async function resolvePhotos(ids) {
  const out = [];
  for (const id of ids || []) {
    if (!isLocal(id)) {
      out.push(id);
      continue;
    }
    const known = photoMap()[id];
    if (known) {
      out.push(known);
      continue;
    }
    const blob = await getLocal(id);
    if (!blob) continue;
    const fd = new FormData();
    fd.append("file", blob, "photo.jpg");
    const { data } = await api.post("/photos", fd);
    localStorage.setItem(PMAP, JSON.stringify({ ...photoMap(), [id]: data.id }));
    out.push(data.id);
  }
  return out;
}

export const forgetLocalPhotos = (ids) => {
  const m = photoMap();
  (ids || []).forEach((id) => delete m[id]);
  localStorage.setItem(PMAP, JSON.stringify(m));
  delLocals(ids);
};

const postWithPhotos = async (path, body) => {
  const r = await api.post(path, body.photo_ids ? { ...body, photo_ids: await resolvePhotos(body.photo_ids) } : body);
  forgetLocalPhotos(body.photo_ids);
  return r;
};

export async function submitOrQueue(path, body, meta) {
  const payload = { ...body, client_id: crypto.randomUUID() };
  if (navigator.onLine !== false) {
    try {
      const r = await postWithPhotos(path, payload);
      return { queued: false, data: r.data };
    } catch (e) {
      if (!isNetErr(e)) throw e;
    }
  }
  writeQueue([...readQueue(), { id: payload.client_id, path, body: payload, ...meta, queued_at: new Date().toISOString() }]);
  return { queued: true };
}

let flushing = false;
export async function flushQueue(userId) {
  const result = { synced: 0, failed: [] };
  if (flushing || navigator.onLine === false) return result;
  flushing = true;
  try {
    for (const item of readQueue().filter((i) => i.user_id === userId)) {
      try {
        await postWithPhotos(item.path, item.body);
        result.synced += 1;
      } catch (e) {
        if (isNetErr(e)) break;
        forgetLocalPhotos(item.body.photo_ids);
        result.failed.push({ ...item, error: errMsg(e) });
      }
      writeQueue(readQueue().filter((i) => i.id !== item.id));
    }
  } finally {
    flushing = false;
  }
  return result;
}

export function useQueue() {
  const [q, setQ] = useState(readQueue);
  useEffect(() => {
    const on = () => setQ(readQueue());
    window.addEventListener("ka-queue", on);
    return () => window.removeEventListener("ka-queue", on);
  }, []);
  return q;
}

export const clearOfflineCache = () => Object.keys(localStorage).filter((k) => k.startsWith("ka_cache:")).forEach((k) => localStorage.removeItem(k));

export function useApi(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params || {});
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    const ck = CACHED.includes(path) ? `ka_cache:${path}:${key}` : null;
    try {
      const r = await api.get(path, { params: JSON.parse(key) });
      setData(r.data);
      if (ck) localStorage.setItem(ck, JSON.stringify(r.data));
    } catch (e) {
      const cached = ck && isNetErr(e) ? localStorage.getItem(ck) : null;
      setData(cached ? JSON.parse(cached) : null);
    } finally {
      setLoading(false);
    }
  }, [path, key]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    window.addEventListener("ka-synced", reload);
    return () => window.removeEventListener("ka-synced", reload);
  }, [reload]);
  return { data, loading, reload, setData };
}

async function post(path, body) {
  return fetch(`${API}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function streamPost(path, body, onChunk) {
  let res = await post(path, body);
  if (res.status === 401) {
    await doRefresh();
    res = await post(path, body);
  }
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(formatDetail(j.detail));
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const d = JSON.parse(p.slice(6));
      if (d.error) throw new Error(d.error);
      if (d.t) onChunk(d.t);
    }
  }
}

export const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const todayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
export const addDaysISO = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
